import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc as firestoreDoc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import {
  ChevronLeft,
  ChevronRight,
  Gamepad2,
  Hash,
  Medal,
  Search,
  Shield,
  Trophy,
} from 'lucide-react';
import { db } from '../../firebase/config';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { MODE_TINTS, modeLabel } from '../../constants/modes';
import { getAllPlayerSpend, getTiers } from '../../firebase/supporterTiers';
import { resolveTier } from '../../constants/supporterTiers';
import TierBadge from '../ui/TierBadge';
import SEO from '../SEO';

const USERS_PER_PAGE = 12;

const initials = (s) => {
  const v = (s || '').trim();
  if (!v) return '?';
  const parts = v.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const countryFlag = (code) => {
  if (!code || code.length !== 2) return null;
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => c.charCodeAt(0) + 127397));
};

const placeAccent = {
  1: 'text-gold',
  2: 'text-silver',
  3: 'text-bronze',
};

const placeLabel = (p) => (p === 1 ? '1st' : p === 2 ? '2nd' : p === 3 ? '3rd' : `${p}th`);

const levenshtein = (a, b) => {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) matrix[i][j] = matrix[i - 1][j - 1];
      else
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
    }
  }
  return matrix[b.length][a.length];
};

const similarity = (a, b) => {
  const longer = a.length >= b.length ? a : b;
  const shorter = a.length >= b.length ? b : a;
  if (!longer.length) return 1;
  return (longer.length - levenshtein(longer, shorter)) / longer.length;
};

const fuzzyScore = (user, term) => {
  const u = user.username?.toLowerCase() || '';
  const bio = user.bio?.toLowerCase() || '';
  let score = 0;
  if (u === term) score += 100;
  if (u.startsWith(term)) score += 50;
  if (u.includes(term)) score += 25;
  if (bio.includes(term)) score += 10;
  score += similarity(u, term) * 20;
  return score;
};

const UserSearch = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [allUsersCache, setAllUsersCache] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [winners, setWinners] = useState([]);
  const [winnersTournament, setWinnersTournament] = useState(null);
  const [winnersLoading, setWinnersLoading] = useState(true);

  const [tiers, setTiers] = useState([]);
  const [spendMap, setSpendMap] = useState({});

  // Supporter tiers + per-player spend — fetched once, resolved per card.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tierList, spend] = await Promise.all([getTiers(), getAllPlayerSpend()]);
        if (cancelled) return;
        setTiers(tierList);
        setSpendMap(spend);
      } catch {
        // tiers are decorative — fail silent
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Recent winners (top row)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setWinnersLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, 'tournamentWinners'), orderBy('awardedAt', 'desc'), limit(20))
        );
        if (cancelled || snap.empty) {
          setWinners([]);
          setWinnersTournament(null);
          return;
        }
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const mostRecent = all[0];
        const recentDate = mostRecent.awardedAt?.toDate
          ? mostRecent.awardedAt.toDate()
          : new Date(mostRecent.awardedAt);
        const sameDay = all.filter((w) => {
          const wd = w.awardedAt?.toDate ? w.awardedAt.toDate() : new Date(w.awardedAt);
          return (
            wd.toDateString() === recentDate.toDateString() &&
            w.tournamentId === mostRecent.tournamentId
          );
        });

        let tournamentInfo = null;
        if (mostRecent.tournamentId) {
          const t = await getDoc(firestoreDoc(db, 'tournaments', mostRecent.tournamentId));
          if (t.exists()) tournamentInfo = { id: t.id, ...t.data() };
        }

        const enriched = await Promise.all(
          sameDay.map(async (w) => {
            const u = await getDoc(firestoreDoc(db, 'userProfiles', w.userId));
            if (!u.exists()) return null;
            return { ...w, ...u.data(), id: w.userId };
          })
        );
        if (cancelled) return;
        setWinnersTournament(tournamentInfo);
        setWinners(enriched.filter(Boolean).sort((a, b) => a.place - b.place));
      } catch {
        // surface gracefully
      } finally {
        if (!cancelled) setWinnersLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchUserTournaments = async (userId) => {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'tournamentWinners'),
          where('userId', '==', userId),
          orderBy('awardedAt', 'desc')
        )
      );
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch {
      return [];
    }
  };

  const fetchPaginated = async (pageNum = 1) => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'userProfiles'), orderBy('createdAt', 'desc'))
      );
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTotalUsers(all.length);
      setAllUsersCache(all);
      const start = (pageNum - 1) * USERS_PER_PAGE;
      const slice = all.slice(start, start + USERS_PER_PAGE);
      const enriched = await Promise.all(
        slice.map(async (u) => ({ ...u, tournaments: await fetchUserTournaments(u.id) }))
      );
      setUsers(enriched);
      setPage(pageNum);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchSearch = async (term) => {
    setLoading(true);
    try {
      const pool = allUsersCache.length
        ? allUsersCache
        : (await getDocs(collection(db, 'userProfiles'))).docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
      if (!allUsersCache.length) setAllUsersCache(pool);
      const ranked = pool
        .map((u) => ({ ...u, score: fuzzyScore(u, term) }))
        .filter((u) => u.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 50);
      const enriched = await Promise.all(
        ranked.map(async (u) => ({ ...u, tournaments: await fetchUserTournaments(u.id) }))
      );
      setUsers(enriched);
      setPage(1);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      const term = searchTerm.trim().toLowerCase();
      if (term) fetchSearch(term);
      else fetchPaginated(1);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const totalPages = useMemo(() => Math.ceil(totalUsers / USERS_PER_PAGE), [totalUsers]);

  // Resolve each visible user's supporter tier once, keyed by user id.
  const tierByUser = useMemo(() => {
    const fallbackTiers = tiers.length ? tiers : undefined;
    const map = {};
    users.forEach((u) => {
      map[u.id] = resolveTier(spendMap[u.id], fallbackTiers);
    });
    return map;
  }, [users, spendMap, tiers]);

  const goToUser = (id) => navigate(`/user/${id}`);

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 pb-24 pt-12 sm:px-6">
      <SEO
        title="Players — KZ Community Directory"
        description="Search competitive KZ players. Browse profiles, modes, achievements and tournament placements across the KZCOMP community."
        canonicalPath="/users"
      />
      <div className="mb-10 flex flex-col gap-3">
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span className="h-px w-6 bg-heat" />
          Roster
        </div>
        <h1 className="font-display text-5xl font-bold tracking-tight sm:text-6xl">Players</h1>
      </div>

      {/* Winners strip */}
      {winnersLoading ? (
        <div className="mb-12 flex gap-3 overflow-x-auto pb-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-28 w-40 shrink-0 animate-pulse rounded-md border border-border/60 bg-muted/10"
            />
          ))}
        </div>
      ) : winners.length > 0 ? (
        <section className="mb-12">
          <div className="mb-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            <Trophy className="h-3 w-3" />
            Latest tournament ·{' '}
            <span className="text-foreground/80">{winnersTournament?.name || 'Recent'}</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {winners.slice(0, 6).map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => goToUser(w.id)}
                className="group flex w-40 shrink-0 flex-col items-center gap-2 rounded-md border border-border/60 bg-background/40 px-4 py-5 text-center transition-colors hover:border-foreground/40"
              >
                <Avatar className="h-12 w-12">
                  {w.profilePicUrl && <AvatarImage src={w.profilePicUrl} alt="" />}
                  <AvatarFallback>{initials(w.username)}</AvatarFallback>
                </Avatar>
                <div
                  className={cn(
                    'inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em]',
                    placeAccent[w.place] || 'text-muted-foreground'
                  )}
                >
                  <Medal className="h-3 w-3" />
                  {placeLabel(w.place)}
                </div>
                <div className="truncate font-display text-sm font-medium">{w.username}</div>
                {w.mode && (
                  <span
                    className={cn(
                      'inline-flex items-center border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em]',
                      MODE_TINTS[w.mode] || 'border-border text-muted-foreground'
                    )}
                  >
                    {modeLabel(w.mode)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/* Search */}
      <div className="relative mb-8 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by username"
          className="pl-9"
        />
      </div>

      <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        {searchTerm ? `Results for "${searchTerm}"` : 'All players'}
        {users.length > 0 && <span className="text-foreground/40"> · {users.length}</span>}
      </div>

      {loading && users.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-48 animate-pulse rounded-md border border-border/60 bg-muted/10"
            />
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-16 text-center text-sm text-muted-foreground">
          {searchTerm ? 'No players match your search.' : 'No players yet.'}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {users.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => goToUser(u.id)}
                className="group flex flex-col gap-4 rounded-md border border-border/60 bg-background/40 p-5 text-left transition-all hover:-translate-y-0.5 hover:border-foreground/40"
              >
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12 shrink-0">
                    {u.profilePicUrl && <AvatarImage src={u.profilePicUrl} alt="" />}
                    <AvatarFallback>{initials(u.username)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-display text-lg font-semibold">
                        {u.username}
                      </span>
                      {u.role === 'admin' && (
                        <Badge variant="live" className="uppercase">
                          <Shield className="h-2.5 w-2.5" />
                          Admin
                        </Badge>
                      )}
                      {tierByUser[u.id] && <TierBadge tier={tierByUser[u.id]} size="sm" />}
                    </div>
                    <div className="mt-1 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      {u.location && (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-base leading-none">{countryFlag(u.location)}</span>
                          {u.countryName || u.location}
                        </span>
                      )}
                      {u.steamLink && <Gamepad2 className="h-3 w-3 text-info" />}
                      {u.discordUsername && (
                        <span
                          className="inline-flex items-center gap-0.5"
                          title={`Discord: ${u.discordUsername}`}
                        >
                          <Hash className="h-3 w-3 text-info" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {u.tournaments?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {u.tournaments.slice(0, 3).map((t) => (
                      <span
                        key={t.id}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-md border border-border/60 bg-muted/20 px-2 py-0.5 text-[10px]',
                          placeAccent[t.place] || 'text-muted-foreground'
                        )}
                      >
                        <Medal className="h-2.5 w-2.5" />
                        <span className="max-w-[8rem] truncate text-foreground/80">
                          {t.tournamentName}
                        </span>
                        {t.mode && (
                          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                            {modeLabel(t.mode)}
                          </span>
                        )}
                      </span>
                    ))}
                    {u.tournaments.length > 3 && (
                      <span className="inline-flex items-center rounded-md border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground">
                        +{u.tournaments.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <p className="mt-auto line-clamp-2 text-sm text-muted-foreground">
                  {u.bio || 'No bio provided.'}
                </p>
              </button>
            ))}
          </div>

          {!searchTerm.trim() && totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchPaginated(page - 1)}
                disabled={page <= 1 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground tabular-nums">
                Page {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchPaginated(page + 1)}
                disabled={page >= totalPages || loading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserSearch;
